import type { ViewMode } from '@ui';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface IndexQueryState {
  q: string;
  filters: Record<string, string[]>;
  page: number;
  pageSize: number;
  view: ViewMode;
  /** Active tab for pages that switch between related lists (e.g. Permits / Licenses). */
  tab?: string;
}

export type IndexQueryPatch = Partial<Omit<IndexQueryState, 'tab'>>;

/** Search, filters, paging and view per index page key. Reset when the page is left (and on reload). */
export type IndexQueriesState = Record<string, IndexQueryState>;

export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_INDEX_QUERY: IndexQueryState = {
  q: '',
  filters: {},
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  view: 'list',
};

const initialState: IndexQueriesState = {};

const entry = (state: IndexQueriesState, key: string) =>
  (state[key] ??= { ...DEFAULT_INDEX_QUERY, filters: {} });

export const indexQuerySlice = createSlice({
  name: 'indexQuery',
  initialState,
  reducers: {
    updateIndexQuery: (state, action: PayloadAction<{ key: string; patch: IndexQueryPatch }>) => {
      const { key, patch } = action.payload;
      const current = entry(state, key);
      if (patch.q !== undefined) current.q = patch.q;
      if (patch.view !== undefined) current.view = patch.view;
      if (patch.pageSize !== undefined) current.pageSize = patch.pageSize;
      if (patch.filters) Object.assign(current.filters, patch.filters);
      // Changing the result set sends the user back to page 1.
      const resetsPage =
        patch.q !== undefined || patch.filters !== undefined || patch.pageSize !== undefined;
      const page = patch.page ?? (resetsPage ? 1 : undefined);
      if (page !== undefined) current.page = Math.max(1, page);
    },
    toggleIndexFilter: (
      state,
      action: PayloadAction<{ key: string; filterId: string; value: string; checked: boolean }>,
    ) => {
      const { key, filterId, value, checked } = action.payload;
      const current = entry(state, key);
      const values = current.filters[filterId] ?? [];
      current.filters[filterId] = checked
        ? [...new Set([...values, value])]
        : values.filter((v) => v !== value);
      current.page = 1;
    },
    /** Search and filters carry over between tabs; paging starts again on page 1. */
    setIndexTab: (state, action: PayloadAction<{ key: string; tab: string }>) => {
      const current = entry(state, action.payload.key);
      current.tab = action.payload.tab;
      current.page = 1;
    },
    resetIndexQuery: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    },
  },
  selectors: {
    selectIndexQuery: (state, key: string) => state[key] ?? DEFAULT_INDEX_QUERY,
  },
});

export const { updateIndexQuery, toggleIndexFilter, setIndexTab, resetIndexQuery } =
  indexQuerySlice.actions;
export const { selectIndexQuery } = indexQuerySlice.selectors;
